import axios from 'axios';
import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { AI_ENHANCEMENT, COMPANIES } from 'app/api-routes';
import {
  enhanceRequest,
  enhanceSuccess,
  enhanceFailure,
  acceptEnhancementRequest,
  acceptEnhancementSuccess,
  acceptEnhancementFailure,
  usageRequest,
  usageSuccess,
  usageFailure,
  settingsRequest,
  settingsSuccess,
  settingsFailure,
  updateSettingsRequest,
  updateSettingsSuccess,
  updateSettingsFailure,
} from './actions';

const SERVER_TO_I18N_ERROR = {
  aiDisabledForAccount: 'ai_disabled_for_account',
  aiQuotaExceeded: 'ai_quota_exceeded',
  aiRequestFailed: 'ai_request_failed',
};

const errorCodeFrom = (err) => {
  const data = err?.response?.data;
  const serverCode = data?.errorCode || data?.errors?.[0]?.code || null;
  return SERVER_TO_I18N_ERROR[serverCode] || (serverCode ? 'ai_request_failed' : null);
};

const selectCompanyID = (state) => state.auth?.companyInfo?.companyID;

function* enhanceSaga({ payload }) {
  const { feature, text, incidentReportID } = payload;
  try {
    const body = {
      feature,
      text,
      ...(incidentReportID != null ? { incidentReportID } : {}),
    };
    const { payload: data, warnings } = yield axios.post(
      `${AI_ENHANCEMENT}/enhance`,
      { payload: body }
    );
    yield put(enhanceSuccess({ ...data, feature }, { warnings }));
  } catch (err) {
    yield put(enhanceFailure({ feature, error: errorCodeFrom(err) }));
  }
}

function* acceptEnhancementSaga({ payload: { logID, accepted, feature } }) {
  try {
    yield axios.post(`${AI_ENHANCEMENT}/log/${logID}/accept`, null, {
      params: { accepted },
    });
    yield put(acceptEnhancementSuccess({ logID, accepted, feature }));
  } catch (err) {
    yield put(acceptEnhancementFailure(err));
  }
}

function* usageSaga() {
  try {
    const { payload, warnings } = yield axios.get(`${AI_ENHANCEMENT}/usage`);
    yield put(usageSuccess(payload, { warnings }));
  } catch (err) {
    yield put(usageFailure(err));
  }
}

function* settingsSaga() {
  try {
    const companyID = yield select(selectCompanyID);
    if (companyID == null) {
      yield put(settingsFailure(new Error('companyID missing')));
      return;
    }
    const { payload, warnings } = yield axios.get(
      `${COMPANIES}/${companyID}/ai-settings`
    );
    yield put(settingsSuccess(payload, { warnings }));
  } catch (err) {
    yield put(settingsFailure(err));
  }
}

function* updateSettingsSaga({ payload }) {
  try {
    const companyID = yield select(selectCompanyID);
    if (companyID == null) {
      yield put(updateSettingsFailure(new Error('companyID missing')));
      return;
    }
    const { warnings } = yield axios.put(
      `${COMPANIES}/${companyID}/ai-settings`,
      { payload }
    );
    yield put(updateSettingsSuccess(payload, { warnings }));
    yield put(settingsRequest());
  } catch (err) {
    yield put(updateSettingsFailure(err));
  }
}

export default function* () {
  yield all([
    takeEvery(enhanceRequest, enhanceSaga),
    takeEvery(acceptEnhancementRequest, acceptEnhancementSaga),
    takeLatest(usageRequest, usageSaga),
    takeLatest(settingsRequest, settingsSaga),
    takeLatest(updateSettingsRequest, updateSettingsSaga),
  ]);
}
